import React from 'react';
import update from 'immutability-helper';

import storage from '@dashboard/services/storage';
import sessionTracker from '@dashboard/services/sessionTracker';

import { login, verifyAuth, getAdminSeller, getAuthContext, switchAuthStore } from './helpers';
import { get } from 'lodash';
import GoogleAuthService from '@dashboard/services/googleAuth';
import { tracking } from '@dashboard/lib/tracking';
import checkIsConnectedToStripe from '@dashboard/services/seller/checkIsConnectedToStripe';
import isFreeSeller from '@dashboard/services/isFreeSeller';
import normalizeSellerPlan from '@dashboard/services/normalizeSellerPlan';
import isPayingSeller from '@repo/checkout/lib/isPayingSeller';
import { AdminRolesEnum } from '@/dashboard/enums/admin-roles';
import getTokenExpiryTimeInSeconds from '@/dashboard/lib/getTokenExpiryTimeInSeconds';
import { buildAuthCookie } from '@dashboard/services/authCookie';
import { clearSession } from '@dashboard/services/clearSession';
import logException from '@/dashboard/services/logException';

const AuthContext = React.createContext();

// localStorage stringifies, so setItem(key, undefined) persists the literal
// "undefined" — a truthy value that then gets spliced into request URLs as a
// seller id. Never persist a falsy id; drop the key instead.
function persistId(key, value) {
  if (value) storage.local.setItem(key, value);
  else storage.local.removeItem(key);
}

// Heals sessions poisoned before persistId existed: strip the value from
// storage too, not just from state, so every other reader (request.js) is
// fixed in the same pass rather than needing a manual "clear site data".
function readStoredId(key) {
  const value = storage.local.getItem(key);

  if (value === 'undefined' || value === 'null') {
    storage.local.removeItem(key);
    return null;
  }

  return value;
}

class AuthProvider extends React.Component {
  state = {
    isAuthenticating: true,
    userId: readStoredId('userId'),
    sellerId: readStoredId('sellerId'),
    userSellerId: readStoredId('userSellerId'),
    isAuth: !!storage.local.getItem('auth_token'),
    user: {},
    seller: {},
    availableStores: [],
    activeSeller: null,
    isSwitchingStore: false,
  };

  componentDidMount() {
    this.verifyAuth();

    const { user } = this.state;

    const token = this.getToken();

    sessionTracker(user && user._id, token);
  }

  componentDidUpdate(prevProps, prevState) {
    const { isAuth, user, seller } = this.state;

    if (!prevState.isAuth && isAuth) {
      this.verifyAuth();
    }

    if (!prevState.user._id && user._id) {
      const token = this.getToken();

      sessionTracker(user && user._id, token);
    }

    if (user) {
      tracking.identifyUser(user._id);
      tracking.updateUser({
        $email: user.email,
        $name: user.name,
        $created: user.createdAt,
        $seller: seller?._id,
      });
    }
  }

  extractResourceFromUrl = pathname => {
    const patterns = [
      { pattern: /\/dashboard\/customers\/([a-f0-9]{24})/, param: 'customerId' },
      { pattern: /\/dashboard\/payments\/([a-f0-9]{24})/, param: 'paymentId' },
      { pattern: /\/dashboard\/subscriptions\/([a-f0-9]{24})/, param: 'subscriptionId' },
      { pattern: /\/dashboard\/submissions\/([a-f0-9]{24})/, param: 'submissionId' },
      { pattern: /\/dashboard\/bookings\/([a-f0-9]{24})/, param: 'bookingId' },
      { pattern: /\/dashboard\/coupons\/([a-f0-9]{24})/, param: 'couponId' },
      { pattern: /\/dashboard\/pages\/([a-f0-9]{24})/, param: 'pageId' },
    ];

    for (const { pattern, param } of patterns) {
      const match = pathname.match(pattern);
      if (match) {
        return { [param]: match[1] };
      }
    }

    return null;
  };

  verifyAuth = async () => {
    const { isAuth } = this.state;
    const token = this.getToken();
    if (isAuth && token) {
      const pathname = window.location.pathname;
      const resourceParams = this.extractResourceFromUrl(pathname);

      // Fire both auth requests in parallel: getAuthContext only needs the
      // token, not verifyAuth's response, so awaiting them serially just
      // doubled the round-trip time gating the whole dashboard on refresh.
      const [response, responseContext] = await Promise.all([
        verifyAuth(token, resourceParams),
        getAuthContext(token).catch(() => ({ data: {} })),
      ]);

      if (response.data) {
        const { availableStores } = responseContext.data;
        const { currentSeller, ...user } = response.data;

        this.setLogged({
          user,
          seller: currentSeller,
          token,
          availableStores: availableStores || [],
        });
      } else {
        this.logout();
      }
    } else {
      this.setState({ isAuthenticating: false });
    }
  };

  switchStore = async (sellerId, options = {}) => {
    const { onError, navigate } = options;

    if (!sellerId) {
      const error = new Error('sellerId is required');
      if (onError) onError(error);
      throw error;
    }

    if (this.state.sellerId === sellerId) {
      return;
    }

    const hasAccess = this.state.availableStores.some(store => store._id === sellerId);
    if (!hasAccess) {
      const error = new Error('You do not have access to this workspace');
      if (onError) onError(error);
      throw error;
    }

    this.setState({ isSwitchingStore: true });

    try {
      const token = this.getToken();
      const response = await switchAuthStore(token, { sellerId });

      const { user } = response.data;
      const seller = await this.loadFullSeller(token, response.data.seller);

      this.updateAuthContext({ user, seller, isSwitchingStore: false });

      if (navigate) {
        const isStoreCreationIncomplete =
          !seller?.industry || !seller?.displayName || !seller?.name;
        if (isStoreCreationIncomplete) {
          navigate('/seller/start?dashboard_create_store=true');
          return;
        }

        const hasActiveSubscription = isPayingSeller(seller);
        const isTrialing = seller?.subscriptionStatus === 'trialing';
        const isFree = isFreeSeller(seller);

        if (!hasActiveSubscription && !isTrialing && !isFree) {
          navigate('/upgrade');
        }

        navigate('/');
        return;
      }
    } catch (error) {
      logException({ message: 'Failed to switch store', error });
      this.setState({ isSwitchingStore: false });
      if (onError) onError(error);
      throw error;
    }
  };

  // The switch response trims the seller (no emailDomain, senderSignature, ...); the session
  // endpoint returns all of it, as a reload would.
  loadFullSeller = async (token, switchedSeller) => {
    try {
      const response = await verifyAuth(token);
      const fullSeller = response.data?.currentSeller;

      return fullSeller?._id === switchedSeller?._id ? fullSeller : switchedSeller;
    } catch (error) {
      logException({ message: 'Failed to load the full seller after switching store', error });
      return switchedSeller;
    }
  };

  updateAuthContext = ({ user, seller, ...additionalState }) => {
    const userSellerId = user.currentSeller?._id || user.currentSeller;

    persistId('sellerId', seller?._id);
    persistId('userId', user._id);
    persistId('userSellerId', userSellerId);

    this.setState({
      user,
      seller: seller || {},
      activeSeller: seller,
      userSellerId,
      sellerId: seller?._id,
      ...additionalState,
    });
  };

  // Returns null when the stores could not be fetched, so callers can tell
  // that apart from a successful fetch that found none — acting on [] after a
  // failure strands an owner who still has stores.
  refreshAvailableStores = async () => {
    const token = this.getToken();
    if (!token) return null;

    try {
      const response = await getAuthContext(token);
      const { availableStores } = response.data;

      this.setState({ availableStores: availableStores || [] });

      return availableStores || [];
    } catch (error) {
      logException({ message: 'Failed to refresh available stores', error });
      return null;
    }
  };

  setLogged = ({ user, seller, token, availableStores = [] }) => {
    persistId('userId', user._id);
    persistId('sellerId', seller?._id);
    persistId('userSellerId', user.currentSeller?._id);
    token && this.setToken(token);

    this.setState({
      isAuthenticating: false,
      isAuth: true,
      userId: user._id,
      userSellerId: seller?._id,
      sellerId: seller?._id,
      user,
      seller: seller || {},
      activeSeller: seller,
      availableStores,
    });

    tracking.identifyUser(user._id);
  };

  login = data =>
    login(data)
      .then(
        response =>
          new Promise(resolve => {
            if (response.auth) {
              const { currentSeller, ...user } = response.data.user;

              this.setToken(response.data.token);

              this.updateAuthContext({
                user,
                seller: currentSeller,
                isAuth: true,
                isAuthenticating: false,
              });
              tracking.identifyUser(response.data.user._id);

              resolve({
                error: '',
                isAuth: true,
                data: response.data,
              });
            }

            throw Object.assign(new Error(response.message), {
              status: response.meta?.status,
            });
          }),
      )
      .catch(err => {
        return {
          error: err.message,
          status: err.status,
          isAuth: false,
        };
      });

  logout = ({ withoutRedirect } = {}) => {
    clearSession();

    tracking.logout();

    if (withoutRedirect !== true) {
      window.location.href = '/';
    }
  };

  logAdminInAsSeller = async sellerId => {
    const token = this.getToken();

    const response = await getAdminSeller(sellerId, token);

    if (response.data) {
      this.setState({ sellerId: response.data._id });
      this.setState({
        user: this.state.user,
        seller: response.data,
      });
    } else {
      window.alert('Unable to fetch seller, check console');
    }
  };

  setToken = token => {
    storage.local.setItem('auth_token', token);
    const expiry = getTokenExpiryTimeInSeconds(token);
    document.cookie = buildAuthCookie(token, expiry);
  };

  getToken = () => storage.local.getItem('auth_token');

  setUser = user => {
    const newState = update(this.state, {
      user: {
        $set: user,
      },
    });

    this.setState(newState);
  };

  setSeller = seller => {
    const newState = update(this.state, {
      seller: {
        $set: seller,
      },
    });

    this.setState(newState);
  };

  mergeSeller = seller => {
    const newState = update(this.state, {
      seller: {
        $merge: seller,
      },
    });

    this.setState(newState);
  };

  // Google OAuth methods
  googleLogin = async (code, redirectURI, marketingConsent = false, inviteToken = null) => {
    try {
      const response = await GoogleAuthService.login(
        code,
        redirectURI,
        marketingConsent,
        inviteToken,
      );

      if (response.requiresAccountLinking) {
        return {
          requiresAccountLinking: true,
          email: response.email,
          hasInvite: response.hasInvite,
          message: response.message,
        };
      }

      if (response.auth && response.data) {
        this.setLogged({
          user: response.data.user,
          seller: response.data.user.currentSeller,
          token: response.data.token,
        });

        return {
          error: '',
          isAuth: true,
          data: response.data,
          isNewUser: response.data.isNewUser,
          accountLinked: response.data.accountLinked,
          inviteAccepted: response.data.inviteAccepted,
          needsOnboarding: response.data.needsOnboarding,
        };
      }

      throw new Error('Unexpected response from server');
    } catch (error) {
      return {
        error: error.message || 'Google login failed',
        isAuth: false,
      };
    }
  };

  googleLinkAccount = async (code, redirectURI, email, password, inviteToken = null) => {
    try {
      const response = await GoogleAuthService.linkAccount(
        code,
        redirectURI,
        email,
        password,
        inviteToken,
      );

      if (response.auth && response.data) {
        this.setLogged({
          user: response.data.user,
          seller: response.data.user.currentSeller,
          token: response.data.token,
        });

        return {
          error: '',
          isAuth: true,
          data: response.data,
          accountLinked: true,
          inviteAccepted: response.data.inviteAccepted,
        };
      }

      throw new Error('Failed to link Google account');
    } catch (error) {
      return {
        error: error.message || 'Account linking failed',
        isAuth: false,
      };
    }
  };

  googleAcceptInvite = async (code, redirectURI, inviteToken, marketingConsent = false) => {
    try {
      const response = await GoogleAuthService.acceptInvite(
        code,
        redirectURI,
        inviteToken,
        marketingConsent,
      );

      if (response.auth && response.data) {
        this.setLogged({
          user: response.data.user,
          seller: response.data.user.currentSeller,
          token: response.data.token,
        });

        return {
          error: '',
          isAuth: true,
          data: response.data,
          isNewUser: response.data.isNewUser,
          accountLinked: response.data.accountLinked,
          inviteAccepted: true,
        };
      }

      throw new Error('Failed to accept invitation');
    } catch (error) {
      return {
        error: error.message || 'Invitation acceptance failed',
        isAuth: false,
      };
    }
  };

  googleUnlinkAccount = async () => {
    try {
      const response = await GoogleAuthService.unlinkAccount();

      if (response.data) {
        // Update the user state with the updated user data
        this.setUser(response.data.user);

        return {
          success: true,
          message: response.data.message,
          user: response.data.user,
        };
      }

      throw new Error('Failed to unlink Google account');
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to unlink Google account',
      };
    }
  };

  render() {
    const {
      isAuth,
      userId,
      sellerId,
      userSellerId,
      user,
      seller,
      isAuthenticating,
      availableStores,
      activeSeller,
      isSwitchingStore,
    } = this.state;

    const isAdmin = isAuth && user && Object.values(AdminRolesEnum).includes(user.role);
    const plan = normalizeSellerPlan(get(seller, 'plan') || {});
    const isTrialing = seller?.subscriptionStatus === 'trialing';
    const isTrialExpired = seller?.subscriptionStatus === 'trial_expired';
    const isCanceled = seller?.subscriptionStatus === 'canceled';
    const isFree = isFreeSeller(seller);
    const isCanceling = !!seller?.stripeSubscriptionCancelAt;
    const hasActiveSubscription = isPayingSeller(seller);
    const isConnectedToStripe = seller?._id ? checkIsConnectedToStripe(seller) : true; // only check if seller exists
    const hasPastDueSubscription = seller?.stripeSubscriptionStatus === 'past_due';

    const userRoleInSeller = seller?.users?.find(u => u.user === userId)?.role;
    const isOwner = userRoleInSeller === 'owner' || isAdmin;
    const isAdminLoggedInAsSeller =
      isAdmin &&
      sellerId !== userSellerId &&
      !availableStores.some(store => store._id === sellerId);

    return (
      <AuthContext.Provider
        value={{
          verifyAuth: this.verifyAuth,
          isAuth,
          userId,
          sellerId,
          userSellerId,
          plan,
          hasActiveSubscription,
          hasPastDueSubscription,
          isConnectedToStripe,
          login: this.login,
          logout: this.logout,
          setToken: this.setToken,
          token: this.getToken(),
          user,
          userRoleInSeller,
          isOwner,
          seller,
          setLogged: this.setLogged,
          setUser: this.setUser,
          setSeller: this.setSeller,
          mergeSeller: this.mergeSeller,
          isAuthenticating,
          isAdmin,
          logAdminInAsSeller: this.logAdminInAsSeller,
          isAdminLoggedInAsSeller,
          isTrialing,
          isTrialExpired,
          isCanceled,
          isCanceling,
          isFree,
          // Google OAuth methods
          googleLogin: this.googleLogin,
          googleLinkAccount: this.googleLinkAccount,
          googleAcceptInvite: this.googleAcceptInvite,
          googleUnlinkAccount: this.googleUnlinkAccount,
          // Multi-seller workspace fields
          availableStores,
          activeSeller,
          switchStore: this.switchStore,
          updateAuthContext: this.updateAuthContext,
          refreshAvailableStores: this.refreshAvailableStores,
          isSwitchingStore,
        }}
      >
        {this.props.children}
      </AuthContext.Provider>
    );
  }
}

const AuthConsumer = AuthContext.Consumer;

function useAuthState() {
  const context = React.useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuthState must be used within a AuthProvider');
  }

  return context;
}

function useAuth() {
  return useAuthState();
}

export { AuthProvider, AuthConsumer, AuthContext, useAuth };
